import { Position, MoveResult } from '../position/position.js';
import { MaterialEvaluator } from '../eval/evaluator.js';

export const MATE_SCORE = 100000;

export class SearchTimeout extends Error {
    constructor() {
        super("Search timeout");
        this.name = "SearchTimeout";
    }
}

export class SearchTimer {
    deadline: number = 0.0;
    node_count: number = 0;
    start_time: number = 0.0;

    start(time_limit: number = 0.0) {
        this.node_count = 0;
        this.start_time = performance.now();
        if (time_limit > 0) {
            this.deadline = this.start_time + time_limit * 1000;
        } else {
            this.deadline = 0.0;
        }
    }

    check() {
        this.node_count++;
        if ((this.node_count & 1023) === 0) {
            if (this.deadline > 0 && performance.now() >= this.deadline) {
                throw new SearchTimeout();
            }
        }
    }

    elapsed(): number {
        return (performance.now() - this.start_time) / 1000;
    }

    is_expired(): boolean {
        if (this.deadline <= 0) return false;
        return performance.now() >= this.deadline;
    }

    should_start_next_iteration(last_iter_time: number): boolean {
        if (this.deadline <= 0) return true;
        const remaining = (this.deadline - performance.now()) / 1000;
        const estimated_next = last_iter_time * 4.0;
        return remaining > estimated_next;
    }
}

const _timer = new SearchTimer();

function _format_move_str(position: Position, pid: number, to_x: number, to_y: number): string {
    const pt = position.get_piece_type(pid);
    const xy = position.get_piece_xy(pid);
    const from_str = xy ? `(${xy[0]},${xy[1]})` : "(??)";
    return `${pt.name}${from_str}→(${to_x},${to_y})`;
}

export function alpha_beta(
    position: Position,
    evaluator: MaterialEvaluator,
    depth: number,
    alpha: number,
    beta: number,
    is_maximizing: boolean
): [number, any | null] {
    _timer.check();

    if (depth <= 0) {
        return [evaluator.score, null];
    }

    const side = position.current_turn;
    const moves = position.generate_all_moves(side);

    if (moves.length === 0) {
        return [evaluator.score, null];
    }

    moves.sort((a, b) => {
        const [, to_x_a, to_y_a, , move_type_a] = a;
        const [, to_x_b, to_y_b, , move_type_b] = b;

        const getScore = (to_x: number, to_y: number, move_type: string) => {
            if (move_type === 'enemy') {
                const target_pid = position.board[to_y * 36 + to_x];
                if (target_pid > 0) {
                    return -position.get_piece_type(target_pid).value;
                }
                return 0;
            }
            return 1000;
        };
        return getScore(to_x_a, to_y_a, move_type_a) - getScore(to_x_b, to_y_b, move_type_b);
    });

    let best_move: any | null = null;
    let best_score: number;

    if (is_maximizing) {
        best_score = -Infinity;
        for (const move of moves) {
            const [pid, to_x, to_y, captured_path, move_type] = move;
            const snapshot = position.apply_move(pid, to_x, to_y, captured_path);
            evaluator.apply_move(snapshot);

            let promoted = false;
            let old_tid: number | undefined;
            let new_tid: number | undefined;

            if (snapshot.can_promote) {
                old_tid = snapshot.pre_promote_type_id;
                new_tid = snapshot.promotion_type_id;
                if (old_tid !== undefined && new_tid !== undefined) {
                    const old_pt = position.piece_types.get(old_tid);
                    const new_pt = position.piece_types.get(new_tid);
                    if (old_pt && new_pt && new_pt.value > old_pt.value) {
                        position.promote_piece(pid);
                        evaluator.apply_promotion(pid, old_tid, new_tid);
                        promoted = true;
                    }
                }
            }

            const [score, ] = alpha_beta(position, evaluator, depth - 1, alpha, beta, false);

            if (promoted && old_tid !== undefined && new_tid !== undefined) {
                evaluator.undo_promotion(pid, old_tid, new_tid);
            }
            evaluator.undo_move(snapshot);
            position.undo();

            if (score > best_score) {
                best_score = score;
                best_move = move;
            }
            alpha = Math.max(alpha, best_score);
            if (beta <= alpha) break;
        }
        return [best_score, best_move];
    } else {
        best_score = Infinity;
        for (const move of moves) {
            const [pid, to_x, to_y, captured_path, move_type] = move;
            const snapshot = position.apply_move(pid, to_x, to_y, captured_path);
            evaluator.apply_move(snapshot);

            let promoted = false;
            let old_tid: number | undefined;
            let new_tid: number | undefined;

            if (snapshot.can_promote) {
                old_tid = snapshot.pre_promote_type_id;
                new_tid = snapshot.promotion_type_id;
                if (old_tid !== undefined && new_tid !== undefined) {
                    const old_pt = position.piece_types.get(old_tid);
                    const new_pt = position.piece_types.get(new_tid);
                    if (old_pt && new_pt && new_pt.value > old_pt.value) {
                        position.promote_piece(pid);
                        evaluator.apply_promotion(pid, old_tid, new_tid);
                        promoted = true;
                    }
                }
            }

            const [score, ] = alpha_beta(position, evaluator, depth - 1, alpha, beta, true);

            if (promoted && old_tid !== undefined && new_tid !== undefined) {
                evaluator.undo_promotion(pid, old_tid, new_tid);
            }
            evaluator.undo_move(snapshot);
            position.undo();

            if (score < best_score) {
                best_score = score;
                best_move = move;
            }
            beta = Math.min(beta, best_score);
            if (beta <= alpha) break;
        }
        return [best_score, best_move];
    }
}

export function search_best_move(
    position: Position,
    max_depth: number = 1,
    time_limit: number = 30.0
): any | null {
    _timer.start(time_limit);

    const board_snapshot = new Int16Array(position.board);
    const turn_snapshot = position.current_turn;

    const is_maximizing = (position.current_turn === 0);
    const evaluator = new MaterialEvaluator(position);

    const side = position.current_turn;
    const moves = position.generate_all_moves(side);
    if (moves.length === 0) return null;

    if (moves.length === 1) {
        console.log(`[Search] Only one legal move, returning immediately.`);
        return {
            best_move: moves[0],
            score: evaluator.score,
            depth_completed: 0,
            nodes: 1,
        };
    }

    console.log(`[Search] Starting search: max_depth=${max_depth}, time_limit=${time_limit}s, moves=${moves.length}, side=${side === 0 ? '先手' : '後手'}`);

    let best_result: any = null;
    let completed_depth = 0;

    for (let current_depth = 1; current_depth <= max_depth; current_depth++) {
        const iter_start = performance.now();
        try {
            const [score, best_move] = alpha_beta(
                position, evaluator, current_depth,
                -Infinity, Infinity, is_maximizing
            );

            const iter_time = (performance.now() - iter_start) / 1000;
            completed_depth = current_depth;

            let move_str = "(none)";
            if (best_move) {
                move_str = _format_move_str(position, best_move[0], best_move[1], best_move[2]);
            }

            console.log(`[Search] depth=${current_depth} score=${score.toFixed(1)} best=${move_str} nodes=${_timer.node_count} time=${iter_time.toFixed(2)}s`);

            best_result = {
                best_move: best_move,
                score: score,
                depth_completed: current_depth,
                nodes: _timer.node_count,
            };

            if (current_depth < max_depth) {
                if (!_timer.should_start_next_iteration(iter_time)) {
                    const elapsed = _timer.elapsed();
                    console.log(`[Search] Stopping at depth=${current_depth} (elapsed=${elapsed.toFixed(2)}s, last_iter=${iter_time.toFixed(2)}s)`);
                    break;
                }
            }

        } catch (e) {
            if (e instanceof SearchTimeout) {
                const elapsed = _timer.elapsed();
                console.log(`[Search] Timeout during depth=${current_depth} after ${elapsed.toFixed(2)}s. Using depth=${completed_depth} results.`);
                break;
            } else {
                throw e;
            }
        }
    }

    const total_elapsed = _timer.elapsed();
    console.log(`[Search] Completed: depth=${completed_depth} total_nodes=${_timer.node_count} total_time=${total_elapsed.toFixed(2)}s`);

    if (turn_snapshot !== position.current_turn) {
        console.error(`[INTEGRITY ERROR] current_turn changed: ${turn_snapshot} -> ${position.current_turn}`);
    }

    const diff_indices: number[] = [];
    for (let i = 0; i < board_snapshot.length; i++) {
        if (board_snapshot[i] !== position.board[i]) {
            diff_indices.push(i);
        }
    }

    if (diff_indices.length > 0) {
        console.error(`[INTEGRITY ERROR] Board corrupted after search! ${diff_indices.length} cells differ:`);
        for (const idx of diff_indices.slice(0, 20)) {
            const x = idx % 36;
            const y = Math.floor(idx / 36);
            const old_pid = board_snapshot[idx];
            const new_pid = position.board[idx];
            
            const old_name = old_pid > 0 ? position.get_piece_type(old_pid).name : "空";
            const new_name = new_pid > 0 ? position.get_piece_type(new_pid).name : "空";
            console.error(`  ({${x}},{${y}}): ${old_name}(pid=${old_pid}) -> ${new_name}(pid=${new_pid})`);
        }
    }

    return best_result;
}
